import {
  formatPartialCoverage,
  type ProductCostRow,
  type ProductCostsSource,
  type ProductCostTypeInfo,
} from "@repo/shared/types";
import { Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useT } from "@/lib/i18n/use-t";
import { type CostOverrideEdit, OverriddenCostValue } from "./cost-override";
import { InfoNote } from "./info-note";
import { T } from "./product-costs-widget.i18n";

type ProductCostsWidgetProps = {
  products: ProductCostRow[];
  costTypes: ProductCostTypeInfo[];
  title?: string;
  /** QL-417 — costTypeId → weighted %; omit (global view) to hide the row. */
  weighted?: Record<string, number | null> | null;
  /** QL-417 — % of portfolio weight with cost data; below 100 shows a note. */
  coveragePct?: number | null;
  source?: ProductCostsSource;
  /**
   * QL-20 — advisor only, on an editable instance: opens the override dialog
   * for a fee whose cost type allows it, or (reset only) for an override that
   * no longer could be made. Absent = read-only (client flow).
   */
  onEditCost?: (edit: CostOverrideEdit) => void;
  /** QL-20 — the tenant's cost_overrides feature is on, so new overrides can be made. */
  canOverride?: boolean;
};

/**
 * Format percentage value for display
 */
function formatPercentage(value: number | null | undefined): string {
  if (value === undefined || value === null) {
    return "–";
  }
  // Show up to 2 decimal places, but trim trailing zeros
  const formatted = value.toFixed(2).replace(/\.?0+$/, "");
  return `${formatted}%`;
}

export function ProductCostsWidget({
  products,
  costTypes,
  title,
  weighted,
  coveragePct,
  source = "new",
  onEditCost,
  canOverride = false,
}: ProductCostsWidgetProps) {
  const t = useT(T);

  const renderCell = (product: ProductCostRow, ct: ProductCostTypeInfo) => {
    const value = product.costValues[ct.id];
    const info = product.overrides?.[ct.id];
    const shown = info ? (
      <OverriddenCostValue
        value={formatPercentage(value)}
        original={formatPercentage(info.originalValue)}
        note={info.note}
      />
    ) : (
      formatPercentage(value)
    );
    const mode = ct.overrideMode ?? "off";
    const resetOnly = !(canOverride && mode !== "off");
    if (!onEditCost || !product.id || value === undefined || (resetOnly && !info)) {
      return shown;
    }
    const productId = product.id;
    return (
      <span className="inline-flex items-center justify-end gap-1">
        {shown}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          tooltip={t.overrideFee(ct.name, product.name)}
          onClick={() =>
            onEditCost({
              request: { kind: "product_cost", productId, costTypeId: ct.id },
              dialog: {
                label: `${ct.name} · ${product.name}`,
                unit: "pct",
                unitLabel: "%",
                value,
                catalogueLabel: formatPercentage(info?.originalValue ?? value),
                mode,
                note: info?.note ?? null,
                overridden: !!info,
                resetOnly,
              },
            })
          }
        >
          <Pencil className="h-3 w-3" />
        </Button>
      </span>
    );
  };

  // Filter cost types to only those that have at least one product with a value
  const activeCostTypes = costTypes.filter((ct) =>
    products.some((p) => p.costValues[ct.id] !== undefined && p.costValues[ct.id] !== null),
  );

  if (products.length === 0) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-muted-foreground">{t.noProductsInPortfolio}</p>
      </Card>
    );
  }

  const partialCoverage = formatPartialCoverage(coveragePct);
  const coverageNote =
    partialCoverage === null
      ? null
      : (source === "existing" ? t.partialCoverageExisting : t.partialCoverageNew)(partialCoverage);

  if (activeCostTypes.length === 0) {
    return (
      <div className="w-full space-y-4">
        <Card className="p-6 text-center">
          <p className="text-sm text-muted-foreground">{t.noCostDataAvailable}</p>
        </Card>
        {coverageNote && <InfoNote text={coverageNote} />}
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <FlowComponentHeader title={title} />

      <Table variant="data">
        <TableHeader>
          <TableRow>
            <TableHead>{t.product}</TableHead>
            {activeCostTypes.map((ct) => (
              <TableHead key={ct.id} className="text-right">
                {ct.name}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((product, index) => (
            // Unmatched existing holdings have no product id.
            <TableRow key={product.id ?? `unmatched-${index}`}>
              <TableCell className="font-medium">
                {product.name}
                {product.isin && (
                  <span className="ml-2 text-xs text-muted-foreground">({product.isin})</span>
                )}
              </TableCell>
              {activeCostTypes.map((ct) => (
                <TableCell key={ct.id} className="text-right font-mono">
                  {renderCell(product, ct)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
        {weighted && (
          <TableFooter>
            <TableRow>
              <TableCell className="font-semibold">{t.weightedCost}</TableCell>
              {activeCostTypes.map((ct) => (
                <TableCell key={ct.id} className="text-right font-mono font-semibold">
                  {formatPercentage(weighted[ct.id])}
                </TableCell>
              ))}
            </TableRow>
          </TableFooter>
        )}
      </Table>

      {coverageNote && <InfoNote text={coverageNote} />}
    </div>
  );
}
